import useChatGuidanceVisibility from '../../components/common/chat/shared/useChatGuidanceVisibility';
import { useCallback, useEffect, useMemo } from 'react';
import { applyResetPage, collectMessageKeys } from '../../components/common/chat/timeline/timelineMerge';
import { openAdminChatFollowupTarget } from '../../components/common/chat/followups/chatFollowupNavigation';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useAdminEventChatMessages(context) {
  const {
    chatTopics,
    ADMIN_MESSAGE_FORMATS,
    API_URL,
    CHAT_FOLLOWUP_AI_TARGET_ID,
    EARLIEST_CREATED_AT,
    RECON_LIMIT,
    anchorHighlightTimerRef,
    bannerTimeoutRef,
    buildImageGroupDisplayMessages,
    buildMessageImageUrl,
    chatContainerRef,
    currentRoomRef,
    dlog,
    fetchEventDetail,
    fetchMessagesRef,
    fetchNewMessagesRef,
    fetchRepresentativeEventIdByThread,
    focusChatAnchorMessage,
    getEventChatMessagesAround,
    hasMore,
    hasNewerMessages,
    info,
    initialLoadRef,
    isAiTargetSelected,
    isEventChatAreaOpenRef,
    isFetching,
    isInitialLoading,
    isNarrow,
    isSearchMode,
    lastBannerIdRef,
    lastDiffFetchRef,
    latestCreatedAtRef,
    limit,
    listEventChatMessages,
    listEventChatNewMessages,
    loadedMessagesEventId,
    loadingNewerMessages,
    loadingOlderMessages,
    loggedInAdminId,
    markEventUnreadLocally,
    markLatestRead,
    mergeMessages,
    messageIdsRef,
    messageWindowOffset,
    messages,
    mobileEventChatHistoryRef,
    mobileEventListPrefRef,
    mobileView,
    navigate,
    nowJstDbString,
    oldestCreatedAtRef,
    openExternalUrl,
    orderFrozen,
    patchMessages,
    pendingRestoreRef,
    pickFirstString,
    pickRealtimeReadCount,
    postEventReadReceipt,
    prevHistoryForChatImageRef,
    pushViewState,
    replaceViewState,
    requestRefreshTop,
    selectedEventId,
    selectedEventIdRef,
    setActiveAnchorMessageId,
    setEvents,
    setHasMore,
    setHasNewerMessages,
    setFollowupRefreshKey,
    setImgModalIndex,
    setImgModalOpen,
    setIsAiTargetSelected,
    setIsFetching,
    setIsInitialLoading,
    setLatestCreatedAt,
    setLoadedMessagesEventId,
    setLoadingNewerMessages,
    setLoadingOlderMessages,
    setMessageWindowOffset,
    messagesRef,
    setMessages,
    setMobileView,
    setOffset,
    setSelectedEventId,
    setSelectedEventInfo,
    setSettingsEvent,
    setShowNewMsgBanner,
    setShowNotes,
    setShowSettingsModal,
    settingsEventIdRef,
    settingsEventRef,
    socket,
    toastError,
  } = context;
    const mapServerMessage = useCallback((m) => {
      const isAdmin = m.message_type === 'admin_message' || Boolean(m.admin_user_id);
      const isSystem = m.message_type === 'system_message';
      const isEventCancelled =
        isSystem &&
        (m.message_format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || m.message_format === 'event_cancelled');
      return { ...m, isAdminMsg: isAdmin, isSystemMsg: isSystem, isEventCancelledMsg: isEventCancelled };
    }, []);

    const buildRealtimeEventTextMessage = useCallback((payload = {}, fallbackEventId = '') => {
      if (payload?.question) return null;
      const format = String(payload?.messageFormat || payload?.message_format || 'text').trim().toLowerCase();
      const messageId = String(payload?.messageId || payload?.message_id || '').trim();
      const content = pickFirstString(
        payload?.content,
        payload?.text,
        payload?.preview,
        payload?.messagePreview,
        payload?.message_preview,
      );
      if (format !== 'text' || !messageId || !content) return null;
      const senderType = String(payload?.senderType || payload?.sender_type || '').trim().toLowerCase();
      const messageType =
        String(payload?.messageType || payload?.message_type || '').trim().toLowerCase() ||
        (senderType === 'admin' ? 'admin_message' : 'artist_message');
      const senderId = payload?.senderId || payload?.sender_id || null;
      const adminUserId =
        messageType === 'admin_message'
          ? payload?.senderAdminId ||
            payload?.sender_admin_id ||
            payload?.adminUserId ||
            payload?.admin_user_id ||
            senderId ||
            null
          : null;
      const artistId =
        messageType === 'artist_message'
          ? payload?.senderArtistId ||
            payload?.sender_artist_id ||
            payload?.artistId ||
            payload?.artist_id ||
            senderId ||
            null
          : null;
      return mapServerMessage({
        topic: payload.topic,
        topic_id: payload.topicId || payload.topic_id,
        topic_name: payload.topicName || payload.topic_name,
        id: messageId,
        event_id: payload?.eventId || payload?.event_id || fallbackEventId || null,
        event_uuid: payload?.eventId || payload?.event_id || fallbackEventId || null,
        source_event_id: payload?.eventId || payload?.event_id || fallbackEventId || null,
        thread_id: payload?.threadId || payload?.thread_id || null,
        message_type: messageType,
        message_format: 'text',
        content,
        admin_user_id: adminUserId,
        artist_id: artistId,
        created_at: new Date().toISOString(),
        is_read: false,
        read_count: pickRealtimeReadCount(payload, 1),
        reactions: [],
        mentions: Array.isArray(payload?.mentions) ? payload.mentions : [],
      });
    }, [mapServerMessage]);

    const fetchMessages = useCallback(async (
      eventId,
      reset = false,
      startOffset = 0,
      { anchorMessageId = '', markRead = true, allowReadWhenOpening = false } = {},
    ) => {
    if (!eventId || isFetching) return;
    const targetEventId = String(eventId || '').trim();
    const normalizedAnchorMessageId = reset ? String(anchorMessageId || '').trim() : '';
    // 往復中に socket で届いた分を後で拾い直すための基準。
    const keysBeforeFetch = collectMessageKeys(messagesRef?.current);
    if (reset) {
      setLoadedMessagesEventId('');
      setIsInitialLoading(true);
    }
    if (!reset) setLoadingOlderMessages(true);
    setIsFetching(true);
    try {
      if (reset) {
        const data = normalizedAnchorMessageId
          ? await getEventChatMessagesAround(eventId, normalizedAnchorMessageId, 25, 25)
          : await listEventChatMessages(eventId, 0, limit);
        const raw = Array.isArray(data.messages) ? data.messages : [];
        if (selectedEventIdRef.current !== eventId) return;
        const mapped = raw.map(mapServerMessage);
        // mapped は wire 順(ASC、§1)。state は DESC(§3.1)なので merge で正規化する。
        // 境界カーソル(latest/oldest)は wire 順の受信ページから読む。
        setMessages((prev) => applyResetPage(prev, mapped, keysBeforeFetch));
        messageIdsRef.current = new Set(mapped.map(m => m.id));
        if (normalizedAnchorMessageId) {
          const windowOffset = Number(data?.offset || 0);
          setMessageWindowOffset(windowOffset);
          setOffset(windowOffset + mapped.length);
          setHasMore(!!data?.hasOlder);
          setHasNewerMessages(!!data?.hasNewer);
          focusChatAnchorMessage({
            messageId: normalizedAnchorMessageId,
            containerRef: chatContainerRef,
            setActiveAnchorMessageId,
            timerRef: anchorHighlightTimerRef,
            behavior: 'auto',
          });
        } else {
          setMessageWindowOffset(0);
          setOffset(mapped.length);
          setHasMore(mapped.length === limit);
          setHasNewerMessages(false);
          setActiveAnchorMessageId('');
        }
        if (mapped.length) {
          setLatestCreatedAt(mapped[mapped.length - 1].created_at);
          oldestCreatedAtRef.current = mapped[0].created_at;
        } else {
          oldestCreatedAtRef.current = null;
        }
        // ANCHOR ジャンプ時は focusChatAnchorMessage が位置決めするため最下部固定しない(§3.4)。
        if (!normalizedAnchorMessageId && chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
        // §3.6: ANCHOR ジャンプ(検索/リプライ)で開いた時は最新未到達のため既読化を保留。
        const shouldMarkRead =
          markRead && !normalizedAnchorMessageId && (isEventChatAreaOpenRef.current || allowReadWhenOpening);
        if (shouldMarkRead) {
          markEventUnreadLocally(eventId);
        }
        dlog('fetchMessages reset ->', { eventId, count: mapped.length });
        const lastMessage = mapped.length ? mapped[mapped.length - 1] : null;
        if (shouldMarkRead && lastMessage) {
          await postEventReadReceipt(eventId, lastMessage.id);
        }
        lastDiffFetchRef.current = Date.now();
        return;
      }

        const normalizedStartOffset = Math.max(0, Number.parseInt(startOffset, 10) || 0);
        const data = await listEventChatMessages(eventId, normalizedStartOffset, limit);
        const raw = Array.isArray(data?.messages) ? data.messages : [];
        if (selectedEventIdRef.current !== eventId) return;
        const mapped = raw.map(mapServerMessage);
        const deduped = mapped.filter(m => !messageIdsRef.current.has(m.id));
        deduped.forEach(m => messageIdsRef.current.add(m.id));
        if (deduped.length > 0) {
          setMessages(prev => mergeMessages(prev, deduped));
          // older ページ(wire ASC)の先頭が新しい global oldest。
          oldestCreatedAtRef.current = deduped[0].created_at;
        }
        setHasMore(mapped.length === limit);
        setOffset(normalizedStartOffset + mapped.length);
        dlog('fetchMessages before ->', { eventId, added: deduped.length, totalFetched: mapped.length });
    } catch (err) {
      if (normalizedAnchorMessageId && Number(err?.status) === 404) {
        try {
          const fallbackData = await listEventChatMessages(eventId, 0, limit);
          const fallbackRaw = Array.isArray(fallbackData?.messages) ? fallbackData.messages : [];
          if (selectedEventIdRef.current !== eventId) return;
          const fallbackMapped = fallbackRaw.map(mapServerMessage);
          setMessages((prev) => applyResetPage(prev, fallbackMapped, keysBeforeFetch));
          messageIdsRef.current = new Set(fallbackMapped.map((m) => m.id));
          setMessageWindowOffset(0);
          setOffset(fallbackMapped.length);
          setHasMore(fallbackMapped.length === limit);
          setHasNewerMessages(false);
          setActiveAnchorMessageId('');
          if (fallbackMapped.length) {
            setLatestCreatedAt(fallbackMapped[fallbackMapped.length - 1].created_at);
            oldestCreatedAtRef.current = fallbackMapped[0].created_at;
          } else {
            oldestCreatedAtRef.current = null;
          }
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
          const shouldMarkRead = markRead && (isEventChatAreaOpenRef.current || allowReadWhenOpening);
          if (shouldMarkRead) {
            markEventUnreadLocally(eventId);
          }
          const lastMessage = fallbackMapped.length ? fallbackMapped[fallbackMapped.length - 1] : null;
          if (shouldMarkRead && lastMessage) {
            await postEventReadReceipt(eventId, lastMessage.id);
          }
          lastDiffFetchRef.current = Date.now();
          info('対象メッセージが見つからないため、最新メッセージを表示しました');
          return;
        } catch (fallbackError) {
          dlog('fetchMessages anchor fallback error', fallbackError);
        }
      }
      dlog('fetchMessages error', err);
    } finally {
      if (!reset) setLoadingOlderMessages(false);
      if (reset && String(selectedEventIdRef.current || '').trim() === targetEventId) {
        setLoadedMessagesEventId(targetEventId);
      }
      setIsFetching(false);
      if (reset) setIsInitialLoading(false);
    }
  }, [info, isFetching, limit, mapServerMessage, markEventUnreadLocally, postEventReadReceipt]);

    useEffect(() => {
      if (!selectedEventId) return;
      if (isFetching) return;
      if (messages.length > 0) return;
      if (initialLoadRef.current.has(selectedEventId)) return;
      initialLoadRef.current.add(selectedEventId);
      fetchMessages(selectedEventId, true, 0, { markRead: isEventChatAreaOpenRef.current });
    }, [fetchMessages, selectedEventId, isFetching, messages.length]);

    const loadNewerMessages = useCallback(async () => {
      if (
        !selectedEventId ||
        loadingNewerMessages ||
        isFetching ||
        !hasNewerMessages
      ) {
        return;
      }

      const nextOffset = Math.max(0, messageWindowOffset - limit);
      const fetchLimit = Math.max(1, messageWindowOffset - nextOffset);
      const c = chatContainerRef.current;
      let anchorId = null;
      let anchorTop = 0;
      if (c) {
        const cRect = c.getBoundingClientRect();
        const rows = Array.from(c.querySelectorAll('[data-mid]'));
        const firstVisible = rows.find((el) => el.getBoundingClientRect().top >= cRect.top - 1);
        if (firstVisible) {
          anchorId = firstVisible.getAttribute('data-mid');
          anchorTop = firstVisible.getBoundingClientRect().top;
        }
      }

      setLoadingNewerMessages(true);
      try {
        const data = await listEventChatMessages(selectedEventId, nextOffset, fetchLimit);
        const raw = Array.isArray(data.messages) ? data.messages : [];
        const mapped = raw.map(mapServerMessage);
        const deduped = mapped.filter((message) => !messageIdsRef.current.has(message.id));
        deduped.forEach((message) => messageIdsRef.current.add(message.id));
        if (deduped.length > 0) {
          setMessages((prev) => mergeMessages(prev, deduped));
        }
        if (mapped.length > 0) {
          setLatestCreatedAt(mapped[mapped.length - 1].created_at);
        }
        setMessageWindowOffset(nextOffset);
        setHasNewerMessages(nextOffset > 0);

        requestAnimationFrame(() => {
          if (!c || !anchorId) return;
          const sameRow = c.querySelector(`[data-mid="${anchorId}"]`);
          if (!sameRow) return;
          const newTop = sameRow.getBoundingClientRect().top;
          const dy = newTop - anchorTop;
          // §4.1.3: column-reverse の標準アンカリングで通常 dy≈0。実測残差が 1px 超のときのみ補正。
          if (Math.abs(dy) > 1) c.scrollTop += dy;
        });
      } catch (error) {
        dlog('loadNewerMessages error', error);
      } finally {
        setLoadingNewerMessages(false);
      }
    }, [
      hasNewerMessages,
      isFetching,
      limit,
      loadingNewerMessages,
      mapServerMessage,
      messageWindowOffset,
      selectedEventId,
    ]);

    const fetchNewMessages = useCallback(async () => {
      const targetEventId = selectedEventIdRef.current;
      if (!targetEventId) return [];

      const since = latestCreatedAtRef.current || EARLIEST_CREATED_AT;
      try {
        const data = await listEventChatNewMessages(targetEventId, since);
        if (!Array.isArray(data.messages) || data.messages.length === 0) return [];

        if (selectedEventIdRef.current !== targetEventId) return [];

        const mapped = data.messages.map(mapServerMessage);
        if (!oldestCreatedAtRef.current && mapped.length) {
          oldestCreatedAtRef.current = mapped[0].created_at;
        }

        // 既存分はフィールド上書き、新規分は追加 — まとめて merge。
        const fresh = mapped.filter(m => {
          if (messageIdsRef.current.has(m.id)) return false;
          messageIdsRef.current.add(m.id);
          return true;
        });
        setMessages(prev => mergeMessages(prev, mapped));

        const lastCreated = mapped[mapped.length - 1].created_at;
        setLatestCreatedAt(lastCreated);
        setOffset(prev => prev + fresh.length);

        const c = chatContainerRef.current;
        if (c) setTimeout(() => c.dispatchEvent(new Event('scroll')), 0);

        dlog('fetchNewMessages ->', { added: fresh.length, total: mapped.length, lastCreated });
        lastDiffFetchRef.current = Date.now();
        return mapped;
      } catch (err) {
        dlog('fetchNewMessages error', err);
        return [];
      }
    }, [mapServerMessage]);
    useEffect(() => {
      fetchMessagesRef.current = fetchMessages;
    }, [fetchMessages]);
    useEffect(() => {
      fetchNewMessagesRef.current = fetchNewMessages;
    }, [fetchNewMessages]);

    const reconcileRecentMessages = useCallback(async () => {
      if (!selectedEventId) return;
      try {
        const { messages: latest = [] } = await listEventChatMessages(
          selectedEventId,
          0,
          Math.max(limit, RECON_LIMIT)
        );
        const mapped = latest.map(mapServerMessage);
        const map = new Map(mapped.map(m => [m.id, m]));
        // read receipt 等の後勝ちフィールドを既存メッセージへ patch のみ(新規追加はしない)。
        setMessages(prev => patchMessages(prev, [...map.keys()], (m) => map.get(m.id) || {}));
        if (mapped.length) {
          setLatestCreatedAt(mapped[mapped.length - 1].created_at);
        }
        dlog('reconcileRecentMessages -> synced', latest.length);
      } catch (err) {
        dlog('reconcileRecentMessages error', err);
      }
    }, [selectedEventId, mapServerMessage]);

    // NOTE(§4.1.2): 旧 scheduleRealtimeEventReconcile(700ms 後の最新ページ再取得 merge)は廃止。
    // socket 受信は enriched=直接 merge / skeleton=messages/new 差分 fetch の 2 経路に集約。
    // read receipt 用の reconcileRecentMessages(150ms, reconcileTimerRef 経由)は別物で維持。

    const bumpEventToTop = useCallback((list, eventId, patch = {}) => {
      const idx = list.findIndex(e => e && e.event_uuid === eventId);
      if (idx === -1) return list;
      const updated = { ...list[idx], ...patch };
      const others = list.slice(0, idx).concat(list.slice(idx + 1));
      return [updated, ...others];
    }, []);

    const updateSidebarAfterSend = useCallback((previewBase) => {
      const preview = previewBase.length > 50 ? `${previewBase.slice(0, 50)}…` : previewBase;
      const nowDb = nowJstDbString();
      if (isSearchMode) {
        setEvents(prev =>
          prev.map(e =>
            e.event_uuid === selectedEventId
              ? { ...e, last_message_preview: preview, last_message_time: nowDb, unreadCount: 0 }
              : e
          )
        );
      } else {
        if (orderFrozen) {
          setEvents(prev => prev.map(e =>
            e.event_uuid === selectedEventId
              ? { ...e, last_message_preview: preview, last_message_time: nowDb, unreadCount: 0 }
              : e
          ));
        } else {
          setEvents(prev =>
            bumpEventToTop(prev, selectedEventId, {
              last_message_preview: preview,
              last_message_time: nowDb,
              unreadCount: 0
            })
          );
        }
      }
      if (selectedEventId) markEventUnreadLocally(selectedEventId);
    }, [bumpEventToTop, isSearchMode, markEventUnreadLocally, orderFrozen, selectedEventId]);

    /* 最後に開いたイベントの記憶 */
    const LAST_GLOBAL_KEY = 'admin-eventchat:last';
    const storageSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch {} try { localStorage.setItem(k, v); } catch {} };
    const storageRemove = (k) => { try { sessionStorage.removeItem(k); } catch {} try { localStorage.removeItem(k); } catch {} };
    const lastKey = useMemo(() => `admin-eventchat:last:${loggedInAdminId ?? 'anon'}`, [loggedInAdminId]);
    const clearLast = useCallback(() => {
      storageRemove(lastKey);
      storageRemove(LAST_GLOBAL_KEY);
      dlog('clearLast', { lastKey, LAST_GLOBAL_KEY });
    }, [lastKey]);
    const saveLast = useCallback((eventId, view = 'chat') => {
      const payload = JSON.stringify({ eventId, view, ts: Date.now() });
      storageSet(LAST_GLOBAL_KEY, payload);
      storageSet(lastKey, payload);
      dlog('saveLast', { eventId, view, lastKey });
    }, [lastKey]);

    useEffect(() => {
      if (!isNarrow || mobileView !== 'list') return;
      if (!mobileEventListPrefRef.current) return;

      pendingRestoreRef.current = false;
      clearLast();

      if (currentRoomRef.current) {
        try { socket.emit('leave_event', currentRoomRef.current); } catch {}
        currentRoomRef.current = null;
      }

      setSelectedEventId(null);
      setIsAiTargetSelected(true);
      setSelectedEventInfo(null);
      setMessages([]);
      messageIdsRef.current = new Set();
      setShowNotes(false);
      setShowSettingsModal(false);
      setSettingsEvent(null);
      settingsEventIdRef.current = null;
      settingsEventRef.current = null;
      setShowNewMsgBanner(false);
      if (bannerTimeoutRef.current) { clearTimeout(bannerTimeoutRef.current); bannerTimeoutRef.current = null; }
      lastBannerIdRef.current = null;
      setOffset(0);
      setHasMore(true);
      setMessageWindowOffset(0);
      setHasNewerMessages(false);
      setActiveAnchorMessageId('');
      setLatestCreatedAt(null);
    }, [isNarrow, mobileView, clearLast]);

    /* イベント選択 */
    const handleSelectEvent = useCallback((uuid, opts = {}) => {
      const { clearUrl = false, anchorMessageId = '' } = opts;
      const normalizedAnchorMessageId = String(anchorMessageId || '').trim();
      if (uuid === selectedEventId && !normalizedAnchorMessageId && !isAiTargetSelected) {
        if (isNarrow && mobileView !== 'chat') {
          mobileEventListPrefRef.current = false;
          if (!mobileEventChatHistoryRef.current) {
            try {
              // まず一覧状態を履歴に積み、その後チャット状態を積む
              if (!(window.history.state && window.history.state.scope === 'admin-event-chat' && window.history.state.view === 'list')) {
                window.history.pushState({ scope: 'admin-event-chat', view: 'list', eventId: null }, '', '');
              }
              window.history.pushState({ scope: 'admin-event-chat', view: 'chat', eventId: uuid }, '', '');
              mobileEventChatHistoryRef.current = true;
            } catch {
              mobileEventChatHistoryRef.current = false;
            }
          }
          setMobileView('chat');
          window.setTimeout(() => {
            markLatestRead();
          }, 0);
        }
        return;
      }

      // 新しくイベントを選んだら、一覧優先フラグをリセット
      setIsAiTargetSelected(false);
      mobileEventListPrefRef.current = false;
      mobileEventChatHistoryRef.current = false;

      if (currentRoomRef.current) socket.emit('leave_event', currentRoomRef.current);
      socket.emit('join_event', uuid);
      currentRoomRef.current = uuid;

      setSelectedEventId(uuid);
      setShowNotes(false);
      setShowSettingsModal(false);
      setSettingsEvent(null);
      settingsEventIdRef.current = null;
      settingsEventRef.current = null;
      setMessages([]);
      messageIdsRef.current = new Set();
      setShowNewMsgBanner(false);
      if (bannerTimeoutRef.current) { clearTimeout(bannerTimeoutRef.current); bannerTimeoutRef.current = null; }
      lastBannerIdRef.current = null;

      setOffset(0);
      setHasMore(true);
      setMessageWindowOffset(0);
      setHasNewerMessages(false);
      setActiveAnchorMessageId('');
      setLatestCreatedAt(null);

      if (!isNarrow) {
        markEventUnreadLocally(uuid);
      }

      // 履歴状態（モバイルは一覧→チャットの2段を積み、戻るで一覧に戻れるように）
      if (isNarrow) {
        try {
          if (!(window.history.state && window.history.state.scope === 'admin-event-chat' && window.history.state.view === 'list')) {
            window.history.pushState({ scope: 'admin-event-chat', view: 'list', eventId: null }, '', '');
          }
          window.history.pushState({ scope: 'admin-event-chat', view: 'chat', eventId: uuid }, '', '');
          mobileEventChatHistoryRef.current = true;
        } catch {
          mobileEventChatHistoryRef.current = false;
        }
      } else {
        pushViewState(uuid, 'chat');
      }
      saveLast(uuid, 'chat');

      // 700px以下はチャット全画面に切り替え
      if (isNarrow) setMobileView('chat');

      if (clearUrl) navigate('/admin/workspaces/event-operations/event-chat', { replace: true });

      fetchEventDetail(uuid);
      fetchMessages(uuid, true, 0, {
        anchorMessageId: normalizedAnchorMessageId,
        allowReadWhenOpening: true,
      })
        .then(() => {
          requestRefreshTop();
          if (isEventChatAreaOpenRef.current) {
            markEventUnreadLocally(uuid);
          }
          // 検索/リプライのアンカージャンプ時は fetchMessages 側が対象位置へスクロールするため、
          // ここで最下部へ飛ばす・既読化する処理を行うとアンカー位置が上書きされてしまう。
          if (normalizedAnchorMessageId) return;
          setTimeout(() => {
            try {
              const c = chatContainerRef.current;
              if (c) c.scrollTop = c.scrollHeight;
              markLatestRead();
            } catch {}
          }, 0);
        })
        .catch(() => {});
      dlog('handleSelectEvent -> selected', uuid);
    }, [
      fetchEventDetail,
      fetchMessages,
      isAiTargetSelected,
      isNarrow,
      markEventUnreadLocally,
      markLatestRead,
      mobileView,
      navigate,
      requestRefreshTop,
      saveLast,
      selectedEventId,
    ]);

    const openAiInbox = useCallback(() => {
      setFollowupRefreshKey((current) => current + 1);
      setIsAiTargetSelected(true);
      if (currentRoomRef.current) {
        try {
          socket.emit('leave_event', currentRoomRef.current);
        } catch {}
        currentRoomRef.current = null;
      }
      if (!isNarrow) return;
      mobileEventListPrefRef.current = false;
      if (!mobileEventChatHistoryRef.current) {
        try {
          window.history.pushState(
            { scope: 'admin-event-chat', view: 'chat', eventId: CHAT_FOLLOWUP_AI_TARGET_ID },
            '',
            '',
          );
          mobileEventChatHistoryRef.current = true;
        } catch {
          mobileEventChatHistoryRef.current = false;
        }
      }
      setMobileView('chat');
      replaceViewState(null, 'chat');
    }, [isNarrow, setFollowupRefreshKey]);

    const handleOpenAiTask = useCallback(
      (task) => {
        return openAdminChatFollowupTarget({
          task,
          currentChannel: 'event',
          navigate,
          openEvent: async ({ eventId, threadId, messageId }) => {
            let targetEventId = eventId;
            if (!targetEventId && threadId && typeof fetchRepresentativeEventIdByThread === 'function') {
              targetEventId = await fetchRepresentativeEventIdByThread(threadId);
            }
            if (!targetEventId) return;
            handleSelectEvent(targetEventId, {
              clearUrl: true,
              anchorMessageId: messageId,
            });
          },
        });
      },
      [fetchRepresentativeEventIdByThread, handleSelectEvent, navigate],
    );

    /* ファイルDL */
    const handleDownload = (type, id, fallbackUrl) => {
      if (id) {
        const url = `${API_URL}/api/event-chat/${type === 'image' ? 'images' : 'files'}/${id}/download`;
        openExternalUrl(url);
      } else if (fallbackUrl) {
        const a = document.createElement('a');
        a.href = fallbackUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      }
    };

    /* 画像モーダル */
    const imageItems = useMemo(
      () =>
        messages
          .filter(m => m.message_format === ADMIN_MESSAGE_FORMATS.IMAGE && (m.image_url || m.image_file_name))
          .map(m => ({
            ...m,
            id: m.image_id || m.id || (m.image_url || buildMessageImageUrl(m.image_file_name, null)),
            messageId: m.id,
            fileName: m.image_file_name,
            fullSrc: m.image_url || buildMessageImageUrl(m.image_file_name, null),
            proxySrc: buildMessageImageUrl(m.image_file_name, 'proxy') || m.image_url || '',
            src: buildMessageImageUrl(m.image_file_name, 'proxy') || m.image_url || '',
            downloadUrl: m.image_url || buildMessageImageUrl(m.image_file_name, null),
          })),
      [messages]
    );
    const displayMessages = useMemo(
      () => buildImageGroupDisplayMessages(messages),
      [messages]
    );
    const selectedEventKey = String(selectedEventId || '').trim();
    const shouldShowChatGuidance = useChatGuidanceVisibility({
      scopeKey: selectedEventKey ? chatTopics.modeKey : '',
      settled: Boolean(chatTopics.loadedMessageModeKey === chatTopics.modeKey && loadedMessagesEventId === selectedEventKey && !isFetching && !isInitialLoading && !loadingOlderMessages && !loadingNewerMessages),
      hasOlder: hasMore,
    });
    const openImageModalByIdOrSrc = (imageId, src) => {
      let idx = -1;
      if (imageId != null) idx = imageItems.findIndex(it => String(it.id) === String(imageId));
      if (idx < 0 && src) idx = imageItems.findIndex(it => it.fullSrc === src || it.proxySrc === src);
      if (idx < 0) idx = 0;
      if (imageItems.length > 0) {
        setImgModalIndex(idx);
        setImgModalOpen(true);
        prevHistoryForChatImageRef.current = window.history.state;
        pushViewState(selectedEventId, 'chat-image');
      }
    };
    const closeImgModal = (viaPop = false) => {
      setImgModalOpen(false);
      if (!viaPop) {
        const prev = prevHistoryForChatImageRef.current;
        if (prev && prev.scope === 'admin-event-chat') {
          replaceViewState(prev.eventId, prev.view || 'chat');
        }
      }
    };

    const handleJumpToReplyMessage = useCallback(async (reply) => {
      const normalizedMessageId = String(reply?.messageId || '').trim();
      if (!selectedEventId || !normalizedMessageId) return;
      if (reply?.isUnavailable) {
        toastError('返信元メッセージが見つかりません。');
        return;
      }
      try { await chatTopics.openMessage(normalizedMessageId); }
    catch (error) { toastError(error.message || '返信元メッセージを開けませんでした。'); }
    }, [chatTopics, fetchMessages, selectedEventId, toastError]);
    const showPrevImg = () => setImgModalIndex(i => (i === 0 ? imageItems.length - 1 : i - 1));
    const showNextImg = () => setImgModalIndex(i => (i === imageItems.length - 1 ? 0 : i + 1));
  return {
    buildRealtimeEventTextMessage,
    fetchMessages,
    fetchNewMessages,
    loadNewerMessages,
    reconcileRecentMessages,
    bumpEventToTop,
    updateSidebarAfterSend,
    clearLast,
    saveLast,
    handleSelectEvent,
    openAiInbox,
    handleOpenAiTask,
    handleDownload,
    imageItems,
    displayMessages,
    shouldShowChatGuidance,
    openImageModalByIdOrSrc,
    closeImgModal,
    handleJumpToReplyMessage,
    showPrevImg,
    showNextImg,
  };
}
